import { Checkbox, Form, Input, InputNumber, Modal, Select } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { DefaultOptionType } from 'antd/lib/select';
import dayjs, { Dayjs } from 'dayjs';
import _ from 'lodash';
import React, { ChangeEvent, ReactElement, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import BorrowerCategoryEnum from '~Api/Application/BorrowerCategoryEnum';
import BorrowerDocTypeEnum from '~Api/Application/BorrowerDocTypeEnum';
import BorrowerTypeEnum from '~Api/Application/BorrowerTypeEnum';
import GenderEnum from '~Api/Application/GenderEnum';
import IApplication from '~Api/Application/IApplication';
import IBorrower from '~Api/Application/IBorrower';
import IncomeTypeEnum from '~Api/Application/IncomeTypeEnum';
import LicenceStateEnum from '~Api/Application/LicenceStateEnum';
import MaritalStatusEnum from '~Api/Application/MaritalStatusEnum';
import ResidencyStatusEnum from '~Api/Application/ResidencyStatusEnum';
import IDealBorrower from '~Api/Deal/IDealBorrower';
import { applicationBorrowersAddAction } from '~Applications/actions';
import { countriesGetAction } from '~Countries/actions';
import ICountry from '~Countries/ICountry';
import { countriesSelector } from '~Countries/selectors';
import DatePicker from '~UI/DatePicker';

const borrowerDocTypeLabels: IDictionary<string> = {
    [BorrowerDocTypeEnum.Full]: 'Full',
    [BorrowerDocTypeEnum.Low]: 'Low',
};

export default function BorrowerAddModal(props: {
    application: IApplication;
    isOpen: boolean;
    onCancel: () => void;
}): ReactElement {
    const { application, isOpen, onCancel } = props;

    const countries: ICountry[] = useSelector(countriesSelector);

    const dispatch: Dispatch = useDispatch();

    const [ abn, setAbn ] = useState<string>(null);
    const [ address, setAddress ] = useState<string>(null);
    const [ businessName, setBusinessName ] = useState<string>(null);
    const [ category, setCategory ] = useState<BorrowerCategoryEnum>(BorrowerCategoryEnum.Borrower);
    const [ creditScore, setCreditScore ] = useState<number>(null);
    const [ dependentNumber, setDependentNumber ] = useState<number>(null);
    const [ dob, setDob ] = useState<string>(null);
    const [ docType, setDocType ] = useState<string>(null);
    const [ email, setEmail ] = useState<string>(null);
    const [ firstName, setFirstName ] = useState<string>(null);
    const [ gender, setGender ] = useState<GenderEnum>(null);
    const [ incomeType, setIncomeType ] = useState<IncomeTypeEnum>(null);
    const [ jobTitle, setJobTitle ] = useState<string>(null);
    const [ lastName, setLastName ] = useState<string>(null);
    const [ licenceNumber, setLicenceNumber ] = useState<string>(null);
    const [ licenceState, setLicenceState ] = useState<LicenceStateEnum>(null);
    const [ maritalStatus, setMaritalStatus ] = useState<MaritalStatusEnum>(null);
    const [ middleName, setMiddleName ] = useState<string>(null);
    const [ monthlyGrossExpenses, setMonthlyGrossExpenses ] = useState<number>(null);
    const [ monthlyGrossTurnover, setMonthlyGrossTurnover ] = useState<number>(null);
    const [ monthlyHouseholdExpenses, setMonthlyHouseholdExpenses ] = useState<number>(null);
    const [ onTitle, setOnTitle ] = useState<boolean>(null);
    const [ otherIncomeType, setOtherIncomeType ] = useState<string>(null);
    const [ passportCountry, setPassportCountry ] = useState<string>(null);
    const [ passportNumber, setPassportNumber ] = useState<string>(null);
    const [ phone, setPhone ] = useState<string>(null);
    const [ residencyStatus, setResidencyStatus ] = useState<ResidencyStatusEnum>(null);
    const [ totalAssets, setTotalAssets ] = useState<number>(null);
    const [ totalLiabilities, setTotalLiabilities ] = useState<number>(null);
    const [ trustName, setTrustName ] = useState<string>(null);
    const [ type, setType ] = useState<BorrowerTypeEnum>(BorrowerTypeEnum.Individual);
    const [ yearlyIncome, setYearlyIncome ] = useState<number>(null);

    useEffect((): void => {
        if (!countries) {
            dispatch(countriesGetAction());
        }
    }, [
        countries,
        dispatch,
    ]);

    const borrowerAdd: (dealBorrower: IDealBorrower, applicationBorrower: IBorrower) => void = useCallback(
        (dealBorrower: IDealBorrower, applicationBorrower: IBorrower): void => {
            dispatch(applicationBorrowersAddAction(application.uuid, dealBorrower, applicationBorrower));
        },
        [
            application,
            dispatch,
        ],
    );

    const onChangeAbn: (event: ChangeEvent<HTMLInputElement>) => void = useCallback(
        (event: ChangeEvent<HTMLInputElement>): void => {
            if (/[^0-9]/.test(event.target.value)) {
                return;
            }

            setAbn(event.target.value.substr(0, 11));
        },
        [],
    );

    const onChangeAddress: (event: ChangeEvent<HTMLInputElement>) => void = useCallback(
        (event: ChangeEvent<HTMLInputElement>): void => {
            setAddress(event.target.value.substr(0, 255));
        },
        [],
    );

    const onChangeBusinessName: (event: ChangeEvent<HTMLInputElement>) => void = useCallback(
        (event: ChangeEvent<HTMLInputElement>): void => {
            setBusinessName(event.target.value.substr(0, 100));
        },
        [],
    );

    const onChangeCategory: (value: BorrowerCategoryEnum) => void = useCallback(
        (value: BorrowerCategoryEnum): void => {
            setCategory(value);
        },
        [],
    );

    const onChangeCreditScore: (value: number) => void = useCallback(
        (value: number): void => {
            if (value < 0 || value > 1200) {
                return;
            }

            setCreditScore(value);
        },
        [],
    );

    const onChangeDependentNumber: (value: number) => void = useCallback(
        (value: number): void => {
            setDependentNumber(value);
        },
        [],
    );

    const onChangeDob: (date: Dayjs) => void = useCallback(
        (date: Dayjs): void => {
            setDob(date ? date.format('YYYY-MM-DD') : null);
        },
        [],
    );

    const onChangeDocType: (value: BorrowerDocTypeEnum) => void = useCallback(
        (value: BorrowerDocTypeEnum): void => {
            setDocType(value);
        },
        [],
    );

    const onChangeEmail: (event: ChangeEvent<HTMLInputElement>) => void = useCallback(
        (event: ChangeEvent<HTMLInputElement>): void => {
            setEmail(event.target.value.substr(0, 100));
        },
        [],
    );

    const onChangeFirstName: (event: ChangeEvent<HTMLInputElement>) => void = useCallback(
        (event: ChangeEvent<HTMLInputElement>): void => {
            setFirstName(event.target.value.substr(0, 50));
        },
        [],
    );

    const onChangeGender: (value: GenderEnum) => void = useCallback(
        (value: GenderEnum): void => {
            setGender(value);
        },
        [],
    );

    const onChangeIncomeType: (value: IncomeTypeEnum) => void = useCallback(
        (value: IncomeTypeEnum): void => {
            setIncomeType(value);
        },
        [],
    );

    const onChangeJobTitle: (event: ChangeEvent<HTMLInputElement>) => void = useCallback(
        (event: ChangeEvent<HTMLInputElement>): void => {
            setJobTitle(event.target.value);
        },
        [],
    );

    const onChangeLastName: (event: ChangeEvent<HTMLInputElement>) => void = useCallback(
        (event: ChangeEvent<HTMLInputElement>): void => {
            setLastName(event.target.value.substr(0, 50));
        },
        [],
    );

    const onChangeLicenceNumber: (event: ChangeEvent<HTMLInputElement>) => void = useCallback(
        (event: ChangeEvent<HTMLInputElement>): void => {
            setLicenceNumber(event.target.value.substr(0, 20));
        },
        [],
    );

    const onChangeLicenceState: (value: LicenceStateEnum) => void = useCallback(
        (value: LicenceStateEnum): void => {
            setLicenceState(value);
        },
        [],
    );

    const onChangeMaritalStatus: (value: MaritalStatusEnum) => void = useCallback(
        (value: MaritalStatusEnum): void => {
            setMaritalStatus(value);
        },
        [],
    );

    const onChangeMiddleName: (event: ChangeEvent<HTMLInputElement>) => void = useCallback(
        (event: ChangeEvent<HTMLInputElement>): void => {
            setMiddleName(event.target.value.substr(0, 255));
        },
        [],
    );

    const onChangeMonthlyGrossExpenses: (value: number) => void = useCallback(
        (value: number): void => {
            if (value < 0 || value > 99999999) {
                return;
            }

            setMonthlyGrossExpenses(value);
        },
        [],
    );

    const onChangeMonthlyGrossTurnover: (value: number) => void = useCallback(
        (value: number): void => {
            if (value < 0 || value > 99999999) {
                return;
            }

            setMonthlyGrossTurnover(value);
        },
        [],
    );

    const onChangeMonthlyHouseholdExpenses: (value: number) => void = useCallback(
        (value: number): void => {
            if (value < 0 || value > 99999999) {
                return;
            }

            setMonthlyHouseholdExpenses(value);
        },
        [],
    );

    const onChangeOtherIncomeType: (event: ChangeEvent<HTMLInputElement>) => void = useCallback(
        (event: ChangeEvent<HTMLInputElement>): void => {
            setOtherIncomeType(event.target.value.substr(0, 100));
        },
        [],
    );

    const onChangeOnTitle: (event: CheckboxChangeEvent) => void = useCallback(
        (event: CheckboxChangeEvent): void => {
            setOnTitle(event.target.checked);
        },
        [],
    );

    const onChangePassportCountry: (value: string) => void = useCallback(
        (value: string): void => {
            setPassportCountry(value.substr(0, 3));
        },
        [],
    );

    const onChangePassportNumber: (event: ChangeEvent<HTMLInputElement>) => void = useCallback(
        (event: ChangeEvent<HTMLInputElement>): void => {
            setPassportNumber(event.target.value.substr(0, 20));
        },
        [],
    );

    const onChangePhone: (event: ChangeEvent<HTMLInputElement>) => void = useCallback(
        (event: ChangeEvent<HTMLInputElement>): void => {
            setPhone(event.target.value.substr(0, 20));
        },
        [],
    );

    const onChangeResidencyStatus: (value: ResidencyStatusEnum) => void = useCallback(
        (value: ResidencyStatusEnum): void => {
            setResidencyStatus(value);
        },
        [],
    );

    const onChangeTotalAssets: (value: number) => void = useCallback(
        (value: number): void => {
            setTotalAssets(value);
        },
        [],
    );

    const onChangeTotalLiabilities: (value: number) => void = useCallback(
        (value: number): void => {
            setTotalLiabilities(value);
        },
        [],
    );

    const onChangeTrustName: (event: ChangeEvent<HTMLInputElement>) => void = useCallback(
        (event: ChangeEvent<HTMLInputElement>): void => {
            setTrustName(event.target.value.substr(0, 100));
        },
        [],
    );

    const onChangeType: (value: BorrowerTypeEnum) => void = useCallback(
        (value: BorrowerTypeEnum): void => {
            setType(value);
        },
        [],
    );

    const onChangeYearlyIncome: (value: number) => void = useCallback(
        (value: number): void => {
            if (value < 0 || value > 99999999) {
                return;
            }

            setYearlyIncome(value);
        },
        [],
    );

    const onClickOk: () => void = useCallback((): void => {
        onCancel();

        setAbn(null);
        setAddress(null);
        setBusinessName(null);
        setCategory(null);
        setCreditScore(null);
        setDependentNumber(null);
        setDob(null);
        setDocType(null);
        setEmail(null);
        setFirstName(null);
        setGender(null);
        setIncomeType(null);
        setJobTitle(null);
        setLastName(null);
        setMaritalStatus(null);
        setMiddleName(null);
        setMonthlyGrossExpenses(null);
        setMonthlyGrossTurnover(null);
        setMonthlyHouseholdExpenses(null);
        setOnTitle(null);
        setOtherIncomeType(null);
        setPhone(null);
        setResidencyStatus(null);
        setTotalAssets(null);
        setTotalLiabilities(null);
        setTrustName(null);
        setType(null);
        setYearlyIncome(null);

        const dealBorrower: IDealBorrower = {
            abn,
            address,
            businessName,
            category,
            dependentNumber,
            dob,
            email,
            firstName,
            formattedName: type === BorrowerTypeEnum.Individual ? `${firstName} ${lastName}` : (
                type === BorrowerTypeEnum.Company ? businessName : trustName
            ),
            gender,
            incomeType,
            jobTitle,
            lastName,
            licenceNumber,
            licenceState,
            maritalStatus,
            middleName,
            monthlyGrossExpenses,
            monthlyGrossTurnover,
            monthlyHouseholdExpenses,
            onTitle,
            otherIncomeType,
            passportCountry,
            passportNumber,
            phone,
            residencyStatus,
            totalAssets,
            totalLiabilities,
            trustName,
            type,
            yearlyIncome,
        };

        const applicationBorrower: IBorrower = {
            creditScore,
            docType,
        };

        borrowerAdd(dealBorrower, applicationBorrower);
    }, [
        abn,
        address,
        borrowerAdd,
        businessName,
        category,
        creditScore,
        dependentNumber,
        dob,
        docType,
        email,
        firstName,
        gender,
        incomeType,
        jobTitle,
        lastName,
        licenceNumber,
        licenceState,
        maritalStatus,
        middleName,
        monthlyGrossExpenses,
        monthlyGrossTurnover,
        monthlyHouseholdExpenses,
        onCancel,
        onTitle,
        otherIncomeType,
        passportCountry,
        passportNumber,
        phone,
        residencyStatus,
        totalAssets,
        totalLiabilities,
        trustName,
        type,
        yearlyIncome,
    ]);

    if (!countries) {
        return null;
    }

    const borrowerTypeOptions: DefaultOptionType[] = [
        {
            label: 'Company',
            value: BorrowerTypeEnum.Company,
        },
        {
            label: 'Individual',
            value: BorrowerTypeEnum.Individual,
        },
        ...(category !== BorrowerCategoryEnum.Trustee ? [
            {
                label: 'Trust',
                value: BorrowerTypeEnum.Trust,
            },
        ] : []),
    ];

    const companyBlock: JSX.Element = type === BorrowerTypeEnum.Company && (
        <React.Fragment>
            <Form.Item className='business-name' label='Company Name'>
                <Input onChange={onChangeBusinessName} value={businessName} />
            </Form.Item>
            <Form.Item className='abn' label='ACN or ABN'>
                <Input onChange={onChangeAbn} value={abn} />
            </Form.Item>
            <Form.Item className='monthly-gross-turnover' label='Monthly Gross Turnover'>
                <InputNumber
                    addonBefore='$'
                    max={99999999}
                    min={0}
                    onChange={onChangeMonthlyGrossTurnover}
                    value={monthlyGrossTurnover}
                />
            </Form.Item>
            <Form.Item className='monthly-gross-expenses' label='Monthly Gross Expenses'>
                <InputNumber
                    addonBefore='$'
                    max={99999999}
                    min={0}
                    onChange={onChangeMonthlyGrossExpenses}
                    value={monthlyGrossExpenses}
                />
            </Form.Item>
            <Form.Item className='total-assets' label='Total Assets'>
                <InputNumber
                    addonBefore='$'
                    min={0}
                    onChange={onChangeTotalAssets}
                    value={totalAssets}
                />
            </Form.Item>
            <Form.Item className='total-liabilities' label='Total Liabilities'>
                <InputNumber
                    addonBefore='$'
                    min={0}
                    onChange={onChangeTotalLiabilities}
                    value={totalLiabilities}
                />
            </Form.Item>
            <Form.Item className='credit-score' label='Credit Score'>
                <InputNumber
                    max={1200}
                    min={0}
                    onChange={onChangeCreditScore}
                    value={creditScore}
                />
            </Form.Item>
        </React.Fragment>
    );

    const borrowerLicenseStateOptions: DefaultOptionType[] = _.values(LicenceStateEnum).map(
        (stateCode: string): DefaultOptionType => ({
            label: stateCode,
            value: stateCode,
        }),
    );

    const borrowerPassportCountryOptions: DefaultOptionType[] = countries.map(
        (country: ICountry): DefaultOptionType => ({
            label: country.name,
            value: country.iso3,
        }),
    );

    const jobTitleBlock: JSX.Element = [
        IncomeTypeEnum.PaygFullTime,
        IncomeTypeEnum.PaygPartTime,
    ].includes(incomeType) && (
        <Form.Item className='job-title' label='Job Title'>
            <Input onChange={onChangeJobTitle} value={jobTitle} />
        </Form.Item>
    );

    const otherIncomeTypeBlock: JSX.Element = incomeType === IncomeTypeEnum.Other && (
        <Form.Item className='otherIncomeType' label='Other Income Details'>
            <Input onChange={onChangeOtherIncomeType} value={otherIncomeType} />
        </Form.Item>
    );

    const selfEmployedBlock: JSX.Element = incomeType === IncomeTypeEnum.SelfEmployed && (
        <React.Fragment>
            <Form.Item className='business-name' label='Business Name'>
                <Input onChange={onChangeBusinessName} value={businessName} />
            </Form.Item>
            <Form.Item className='abn' label='ABN'>
                <Input onChange={onChangeAbn} value={abn} />
            </Form.Item>
        </React.Fragment>
    );

    const individualBlock: JSX.Element = type === BorrowerTypeEnum.Individual && (
        <React.Fragment>
            <Form.Item className='first-name' label='First Name'>
                <Input onChange={onChangeFirstName} value={firstName} />
            </Form.Item>
            <Form.Item className='middle-name' label='Middle Name(s)'>
                <Input onChange={onChangeMiddleName} value={middleName} />
            </Form.Item>
            <Form.Item className='last-name' label='Last Name'>
                <Input onChange={onChangeLastName} value={lastName} />
            </Form.Item>
            <Form.Item className='phone' label='Phone'>
                <Input onChange={onChangePhone} value={phone} />
            </Form.Item>
            <Form.Item className='email' label='Email'>
                <Input onChange={onChangeEmail} value={email} />
            </Form.Item>
            <Form.Item className='dob' label='DOB'>
                <DatePicker
                    format='DD/MM/YYYY'
                    onChange={onChangeDob}
                    value={dob ? dayjs(dob) : null}
                />
            </Form.Item>
            <Form.Item className='gender' label='Gender'>
                <Select onChange={onChangeGender} value={gender}>
                    <Select.Option value={GenderEnum.Female}>Female</Select.Option>
                    <Select.Option value={GenderEnum.Male}>Male</Select.Option>
                </Select>
            </Form.Item>
            <Form.Item className='licence-state' label='Licence State'>
                <Select
                    onChange={onChangeLicenceState}
                    options={borrowerLicenseStateOptions}
                    value={licenceState}
                />
            </Form.Item>
            <Form.Item className='licence-number' label='Licence Number'>
                <Input onChange={onChangeLicenceNumber} value={licenceNumber} />
            </Form.Item>
            <Form.Item className='passport-country' label='Passport Country'>
                <Select
                    onChange={onChangePassportCountry}
                    options={borrowerPassportCountryOptions}
                    value={passportCountry}
                />
            </Form.Item>
            <Form.Item className='passport-number' label='Passport Number'>
                <Input onChange={onChangePassportNumber} value={passportNumber} />
            </Form.Item>
            <Form.Item className='residency-status' label='Residency Status'>
                <Select onChange={onChangeResidencyStatus} value={residencyStatus}>
                    <Select.Option value={ResidencyStatusEnum.Resident}>Resident</Select.Option>
                    <Select.Option value={ResidencyStatusEnum.NonResident}>Non-Resident</Select.Option>
                </Select>
            </Form.Item>
            <Form.Item className='address' label='Address'>
                <Input onChange={onChangeAddress} value={address} />
            </Form.Item>
            <Form.Item className='dependent-number' label='Number of Dependents'>
                <Select onChange={onChangeDependentNumber} value={dependentNumber}>
                    {_.times(11, (n: number): ReactElement => <Select.Option key={n} value={n}>{n}</Select.Option>)}
                </Select>
            </Form.Item>
            <Form.Item className='marital-status' label='Marital Status'>
                <Select onChange={onChangeMaritalStatus} value={maritalStatus}>
                    <Select.Option value={MaritalStatusEnum.Single}>Single</Select.Option>
                    <Select.Option value={MaritalStatusEnum.Married}>Married</Select.Option>
                    <Select.Option value={MaritalStatusEnum.DeFacto}>De Facto</Select.Option>
                    <Select.Option value={MaritalStatusEnum.Separated}>Separated</Select.Option>
                    <Select.Option value={MaritalStatusEnum.Divorced}>Divorced</Select.Option>
                </Select>
            </Form.Item>
            <Form.Item className='income-type' label='Income Type'>
                <Select onChange={onChangeIncomeType} value={incomeType}>
                    <Select.Option value={IncomeTypeEnum.PaygFullTime}>PAYG - Full Time</Select.Option>
                    <Select.Option value={IncomeTypeEnum.PaygPartTime}>PAYG - Part Time</Select.Option>
                    <Select.Option value={IncomeTypeEnum.SelfEmployed}>Self Employed</Select.Option>
                    <Select.Option value={IncomeTypeEnum.Other}>Other</Select.Option>
                </Select>
            </Form.Item>
            {otherIncomeTypeBlock}
            {jobTitleBlock}
            {selfEmployedBlock}
            <Form.Item className='yearly-income' label='Yearly Income'>
                <InputNumber
                    addonBefore='$'
                    max={99999999}
                    min={0}
                    onChange={onChangeYearlyIncome}
                    value={yearlyIncome}
                />
            </Form.Item>
            <Form.Item className='monthly-household-expenses' extra='Monthly' label='Household Expenses'>
                <InputNumber
                    addonBefore='$'
                    max={99999999}
                    min={0}
                    onChange={onChangeMonthlyHouseholdExpenses}
                    value={monthlyHouseholdExpenses}
                />
            </Form.Item>
            <Form.Item className='total-assets' label='Total Assets'>
                <InputNumber
                    addonBefore='$'
                    min={0}
                    onChange={onChangeTotalAssets}
                    value={totalAssets}
                />
            </Form.Item>
            <Form.Item className='total-liabilities' label='Total Liabilities'>
                <InputNumber
                    addonBefore='$'
                    min={0}
                    onChange={onChangeTotalLiabilities}
                    value={totalLiabilities}
                />
            </Form.Item>
            <Form.Item className='credit-score' label='Credit Score'>
                <InputNumber
                    max={1200}
                    min={0}
                    onChange={onChangeCreditScore}
                    value={creditScore}
                />
            </Form.Item>
        </React.Fragment>
    );

    const trustBlock: JSX.Element = type === BorrowerTypeEnum.Trust && (
        <React.Fragment>
            <Form.Item className='trust-name' label='Trust Name'>
                <Input onChange={onChangeTrustName} value={trustName} />
            </Form.Item>
            <Form.Item className='abn' label='ACN or ABN'>
                <Input onChange={onChangeAbn} value={abn} />
            </Form.Item>
            <Form.Item className='total-assets' label='Total Assets'>
                <InputNumber
                    addonBefore='$'
                    min={0}
                    onChange={onChangeTotalAssets}
                    value={totalAssets}
                />
            </Form.Item>
            <Form.Item className='total-liabilities' label='Total Liabilities'>
                <InputNumber
                    addonBefore='$'
                    min={0}
                    onChange={onChangeTotalLiabilities}
                    value={totalLiabilities}
                />
            </Form.Item>
        </React.Fragment>
    );

    const borrowerDocTypeOptions: DefaultOptionType[] = _.keys(borrowerDocTypeLabels).map(
        (docType: BorrowerDocTypeEnum): DefaultOptionType => ({
            label: borrowerDocTypeLabels[docType],
            value: docType,
        }),
    );

    return (
        <Modal
            onCancel={onCancel}
            onOk={onClickOk}
            open={isOpen}
            title='Add Borrower'
            wrapClassName='application-borrower-modal'
        >
            <Form.Item className='category' label='Category'>
                <Select onChange={onChangeCategory} value={category}>
                    <Select.Option value={BorrowerCategoryEnum.Borrower}>Borrower</Select.Option>
                    <Select.Option value={BorrowerCategoryEnum.Guarantor}>Guarantor</Select.Option>
                    <Select.Option value={BorrowerCategoryEnum.Trustee}>Trustee</Select.Option>
                </Select>
            </Form.Item>
            <Form.Item className='type' label='Type'>
                <Select
                    onChange={onChangeType}
                    options={borrowerTypeOptions}
                    value={type}
                />
            </Form.Item>
            {companyBlock}
            {individualBlock}
            {trustBlock}
            <Form.Item className='doc-type' label='Doc Type'>
                <Select
                    onChange={onChangeDocType}
                    options={borrowerDocTypeOptions}
                    value={docType}
                />
            </Form.Item>
            <Form.Item className='on-title' label='On Title'>
                <Checkbox checked={onTitle} onChange={onChangeOnTitle} />
            </Form.Item>
        </Modal>
    );
}
